import {
  Badge,
  Box,
  Divider,
  IconButton,
  Toolbar as MuiToolbar,
  Tooltip,
  useTheme,
} from '@mui/material';
import { FilterSearch, Setting2 } from 'iconsax-react';
import type { ReactElement } from 'react';

import type { FilterByOpts } from '@web/utils/ApiClient';

import { ButtonWithPopover } from '../../../ButtonWithPopover/ButtonWithPopover';
import InputSearch from '../../../InputSearch/InputSearch';
import { LoadingSpinner } from '../../../LoadingSpinner/LoadingSpinner';
import type { Column } from '../../TableDataModel';
import type { ReadableSet } from '../../useApiTableData';
import { ColumnConfigurationForm } from '../ColumnConfiguration/ColumnConfigurationForm';
import { ClearButton } from './ClearButton';
import { ExportButton } from './ExportButton';
import { PrintButton } from './PrintButton';
import { SaveButton } from './SaveButton';

type PrintOpts = {
  getAllRows: () => Promise<any[]>;
  tableName: string;
};

type ExportOpts<TRow extends { id: string }> = {
  getAllRows: () => Promise<TRow[]>;
  allFilters: FilterByOpts<TRow>[];
  title: string;
};

type ToolbarProps<TRow extends { id: string }> = {
  initialSearchString: string;
  onSearch: (arg0: string) => void;
  allColumns: ReadableSet<Column<TRow>>;
  columnsFiltersCount: number;
  onColumnsFilterIconClick: () => void;
  renderedColumns: ReadableSet<Column<TRow>>;
  isFetchingRows: boolean;
  printOpts?: PrintOpts;
  exportOpts?: ExportOpts<TRow>;
  onColumnChange: (columnID: string, isRendered: boolean) => void;
  onColumnReorder: (newColumns: Column<TRow>[]) => void;
  toolbarFiltersSlot?: ReactElement;
  tableName?: string;
  onSave: () => void;
  onClear: () => void;
};

export const Toolbar = <TRow extends { id: string }>({
  initialSearchString,
  onSearch,
  allColumns: allColumnsSet,
  renderedColumns,
  isFetchingRows,
  printOpts,
  exportOpts,
  onColumnChange,
  onColumnReorder,
  toolbarFiltersSlot,
  tableName,
  columnsFiltersCount = 0,
  onColumnsFilterIconClick,
  onSave,
  onClear,
}: ToolbarProps<TRow>): ReactElement => {
  const theme = useTheme();
  const hasColumnFilters = columnsFiltersCount > 0;

  return (
    <Box pb='1.25rem' bgcolor={theme.palette.SlabGray['50']}>
      <MuiToolbar
        sx={{
          flexWrap: 'wrap',
          gap: '1rem',
          borderRadius: '0.625rem',
          bgcolor: theme.palette.SlabGray[0],
          justifyContent: 'space-between',
          padding: '1rem',
          boxShadow: theme.shadows[1],

          [theme.breakpoints.down('lg')]: {
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
          },
        }}
      >
        <InputSearch
          initialValue={initialSearchString}
          onChange={(v): void => onSearch(v)}
          sx={{
            [theme.breakpoints.down('xl')]: {
              width: '100%',
            },
          }}
        />

        <Box
          flex={1}
          display='flex'
          gap='1rem'
          justifyContent='flex-end'
          sx={{
            [theme.breakpoints.down('lg')]: {
              flexWrap: 'wrap',
              width: '100%',
            },
          }}
        >
          {isFetchingRows && (
            <LoadingSpinner
              color='inherit'
              size={24}
              sx={{ margin: 'auto 8px' }}
              wrapInBox={false}
            />
          )}
          {toolbarFiltersSlot}
        </Box>

        <Box display='flex' gap='1rem' flexWrap='wrap'>
          <Tooltip title='Reset Column Filters'>
            <Badge badgeContent={columnsFiltersCount} color='primary'>
              <IconButton onClick={onColumnsFilterIconClick} disabled={!hasColumnFilters}>
                <FilterSearch
                  variant='TwoTone'
                  color={
                    hasColumnFilters
                      ? theme.palette.SlabIndigo['700']
                      : theme.palette.SlabGray['400']
                  }
                />
              </IconButton>
            </Badge>
          </Tooltip>
          <Tooltip title='Configure Columns'>
            <span>
              <ButtonWithPopover
                startIcon={<Setting2 variant='TwoTone' color={theme.palette.SlabIndigo['700']} />}
              >
                <ColumnConfigurationForm
                  columns={[...allColumnsSet]}
                  renderedColumns={[...renderedColumns]}
                  onColumnChange={onColumnChange}
                  onOrderChange={onColumnReorder}
                />
              </ButtonWithPopover>
            </span>
          </Tooltip>
          {tableName !== undefined && (
            <>
              <SaveButton onSave={onSave} />
              <ClearButton onClear={onClear} />
            </>
          )}
          {(printOpts !== undefined || exportOpts !== undefined) && (
            // The theme's uneven vertical Divider margins would push it off-center.
            <Divider orientation='vertical' flexItem sx={{ my: '0.5rem' }} />
          )}
          {printOpts !== undefined && (
            <PrintButton
              renderedColumns={renderedColumns}
              getAllRows={printOpts.getAllRows}
              tableName={printOpts.tableName}
            />
          )}
          {exportOpts !== undefined && (
            <ExportButton
              renderedColumns={renderedColumns}
              allFilters={exportOpts.allFilters}
              getAllRows={exportOpts.getAllRows}
              title={exportOpts.title}
            />
          )}
        </Box>
      </MuiToolbar>
    </Box>
  );
};
